// src/config/siteConfig.ts
//
// Single source of truth for all site content.
// Positioning: Forward Deployed Engineer / AI Systems Engineer.
// Claims here are kept at a level that is defensible in a technical interview.

export type BlogBlock =
  | { type: "h2"; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "code"; lang?: string; code: string };

export type Blog = {
  slug: string;
  title: string;
  date: string;
  tags: string[];
  excerpt: string;
  readingTimeMins?: number;
  hero?: string;
  content: BlogBlock[];
};

export type ProjectCategory =
  | "Client work"
  | "Platform"
  | "Product"
  | "Open source"
  | "Lab";

export type ProjectStatus =
  | "In production"
  | "Active build"
  | "Research"
  | "Archived";

export type Metric = { value: string; label: string };

export type Project = {
  slug: string;
  title: string;
  kicker: string;
  summary: string;
  category: ProjectCategory;
  status: ProjectStatus;
  period?: string;
  visibility: "public" | "private";
  repo?: string;
  live?: string;
  problem?: string;
  approach: string[];
  challenges?: string[];
  optimizations?: string[];
  impact?: string;
  metrics?: Metric[];
  tech: string[];
  tags?: string[];
  images?: string[];
};

export type Experience = {
  company: string;
  role: string;
  period: string;
  domain: string;
  context?: string;
  clients?: string[];
  bullets: string[];
  tech?: string[];
};

export const siteConfig = {
  // ---- Branding ----
  name: "Raviteja NB",
  authorName: "Raviteja NB",
  AuthorName: "Raviteja NB", // legacy key, still read by some components
  initials: "RN",

  role: "Forward Deployed Engineer",
  tagline: "Forward Deployed Engineer · AI Systems",
  seoTitle:
    "Raviteja NB — Forward Deployed Engineer · AI Systems Engineer",

  headline: "I own products end to end.",
  subheadline:
    "Data pipelines, models, APIs, interfaces, and the security and governance around them — built to survive contact with real users.",

  description:
    "Forward Deployed Engineer and AI systems engineer. I build generative-AI and data-intensive products end to end — retrieval and agent workflows, streaming data platforms, forecasting models, full-stack applications, and the access control, observability, and governance that make them deployable inside an enterprise.",

  email_g: "ravitejanb@gmail.com",
  resumeUrl: "/Raviteja_NB_AI_Systems_Engineer.pdf",

  links: {
    email: "mailto:ravitejanb@gmail.com",
    phone: "+91-9940599597",
    github: "https://github.com/ravic8",
    linkedin: "https://www.linkedin.com/in/raviteja-n-b-aab389119/",
    code360:
      "https://www.naukri.com/code360/profile/7f5f63ae-dd85-4ccb-ad7b-2371ea6dd314",
    leetcode: "https://leetcode.com/u/ravitejanb/",
  },

  // ---- Headline metrics (all traceable to shipped work) ----
  metrics: [
    { value: "12.5M+", label: "telemetry events / day" },
    { value: "150k+", label: "devices instrumented" },
    { value: "5,000+", label: "instruments researched" },
    { value: "10+ yrs", label: "professional experience" },
  ] as Metric[],

  // ---- What I actually do, in order of depth ----
  capabilities: [
    {
      title: "GenAI & agent systems",
      depth: "Primary focus",
      body:
        "Source-grounded RAG, stateful LangGraph workflows, tool calling, structured outputs, and human-in-the-loop review. Retrieval built with document-aware chunking, vector storage, metadata filtering, and citation-backed generation.",
      items: [
        "LangGraph",
        "RAG",
        "Qdrant",
        "Reranking / MMR",
        "Tool calling",
        "Structured outputs",
      ],
    },
    {
      title: "Data platform engineering",
      depth: "Deep",
      body:
        "High-throughput streaming and batch pipelines, columnar warehouses tuned for sub-second analytics, and orchestration that recovers from its own failures.",
      items: [
        "Kafka",
        "Airflow",
        "Dagster",
        "ClickHouse",
        "TimescaleDB",
        "Redshift",
        "AWS",
      ],
    },
    {
      title: "Time-series ML",
      depth: "Deep",
      body:
        "Forecasting and anomaly detection evaluated the way production demands — walk-forward validation, rolling backtests, early-warning lead time, and false-alert rate rather than headline accuracy.",
      items: [
        "ARIMA / Auto-ARIMA",
        "LightGBM",
        "LSTM",
        "Prophet",
        "Anomaly detection",
        "MLflow",
      ],
    },
    {
      title: "Full-stack delivery",
      depth: "Working",
      body:
        "The application layer that makes a model useful: APIs, interfaces, auth, and deployment. Enough front-end to ship the whole product rather than hand off a notebook.",
      items: [
        "Python",
        "FastAPI",
        "Flask",
        "React",
        "TypeScript",
        "Next.js",
        "Rust",
      ],
    },
    {
      title: "Security & governance",
      depth: "Growing",
      body:
        "Tenant isolation, SSO and role-based access, auditability, and application security testing — treated as part of the build rather than a review at the end.",
      items: [
        "Entra ID SSO",
        "RBAC",
        "Tenant isolation",
        "App security testing",
        "Audit trails",
      ],
    },
  ],

  now: [
    "Building an enterprise GenAI platform for SAP delivery teams — retrieval, agent workflows, and the app around them.",
    "Running application security testing and threat modelling on a production system at work.",
    "MSc Financial Engineering at WorldQuant University (2025–2027).",
    "Developing a systematic trade research platform across NSE, TSX, and US equities.",
  ],

  // ---- Legacy keys kept so older components keep compiling ----
  wins: [
    "12.5M+ telemetry events/day across 150k+ devices.",
    "Source-grounded RAG with citation-backed generation in enterprise delivery.",
    "Top 20% globally in WorldQuant BRAIN IQC 2024.",
  ],
  current: [
    "Enterprise GenAI platform for SAP delivery intelligence.",
    "Application security testing on a production system.",
  ],
  home: {
    card1_des:
      "I build generative-AI and data-intensive products end to end — retrieval and agent workflows, streaming platforms, forecasting models, and the governance that makes them deployable.",
    whatIDo: [
      "Source-grounded RAG and stateful agent workflows.",
      "Streaming data platforms and sub-second analytics.",
      "Time-series forecasting and anomaly detection in production.",
    ],
    recentWins: [
      "12.5M+ events/day across 150k+ devices.",
      "Citation-backed RAG for enterprise document intelligence.",
      "Top 20% globally, WorldQuant BRAIN IQC 2024.",
    ],
    currently: [
      "Enterprise GenAI platform for SAP delivery intelligence.",
      "Application security testing on a production system.",
    ],
  },

  // ---- About ----
  about: {
    summary: [
      "I am a forward deployed engineer: I take a product from the data layer all the way to the interface a customer actually uses, and I stay accountable for it once it is deployed inside their environment.",
      "Across 10+ years of professional work — roughly five of them in data-intensive backend and AI/ML engineering, with the last two focused on generative and agentic AI — I have shipped retrieval and agent systems, streaming telemetry platforms processing millions of events a day, forecasting models evaluated against production failure modes, and the full-stack applications that wrap all of it.",
      "My modelling depth is in time series — forecasting, anomaly detection, and backtesting — and I work comfortably with PyTorch and TensorFlow where a problem calls for them. My broader strength is systems: knowing how retrieval, orchestration, storage, serving, and access control fit together, and being the person who makes them fit.",
      "Before engineering, I spent five years at Coal India Limited running industrial operations and procurement. That is where I learned how large organisations actually make decisions — useful context when the job is deploying software into one.",
    ],

    // Honest depth labels. Order matters: strongest first.
    skills: [
      {
        title: "GenAI & agentic systems",
        items: [
          "LangGraph",
          "RAG pipelines",
          "Document intelligence",
          "Tool calling",
          "Structured outputs",
          "Prompt orchestration",
          "Human-in-the-loop",
          "OpenAI / OpenRouter APIs",
        ],
      },
      {
        title: "Retrieval & vector search",
        items: [
          "Embeddings",
          "Qdrant",
          "Semantic & hybrid retrieval",
          "Reranking",
          "MMR diversification",
          "Document-aware chunking",
          "Metadata filtering",
          "Citation grounding",
        ],
      },
      {
        title: "LLMOps & evaluation",
        items: [
          "Langfuse",
          "OpenTelemetry",
          "Distributed tracing",
          "Prompt & workflow tracing",
          "Response evaluation",
          "Token & latency tracking",
          "Feedback capture",
        ],
      },
      {
        title: "Time-series ML",
        items: [
          "ARIMA / Auto-ARIMA",
          "LightGBM",
          "LSTM",
          "Prophet",
          "Croston / TSB",
          "Anomaly detection",
          "Walk-forward validation",
          "Rolling backtests",
          "MLflow",
          "SageMaker",
          "PyTorch / TensorFlow",
        ],
      },
      {
        title: "Data platform",
        items: [
          "Kafka",
          "Airflow",
          "Dagster",
          "ClickHouse",
          "TimescaleDB",
          "Redshift",
          "PostgreSQL",
          "Spark / Hudi",
          "AWS S3 / Glue / Lambda / Step Functions",
          "dbt",
        ],
      },
      {
        title: "Backend & full stack",
        items: [
          "Python",
          "FastAPI",
          "Flask",
          "React",
          "TypeScript",
          "Next.js",
          "Rust",
          "REST",
          "Docker",
          "Terraform",
          "GitHub Actions",
        ],
      },
      {
        title: "Security & governance",
        items: [
          "Microsoft Entra ID SSO",
          "Project-level RBAC",
          "Tenant-aware retrieval",
          "Application security testing",
          "Threat modelling",
          "Site-to-site VPN delivery",
          "Audit trails",
        ],
      },
    ],

    education: [
      "M.Sc. Financial Engineering — WorldQuant University (2025–2027, ongoing)",
      "B.Tech & M.Tech — IIT Madras (2011–2016)",
    ],

    certifications: [
      "Data Structures and Algorithms — Coding Ninjas (Aug 2024)",
      "Introduction to Python — Coding Ninjas (Apr 2024)",
      "Supervised Machine Learning: Regression & Classification — DeepLearning.AI (Feb 2024)",
    ],

    achievements: [
      "WorldQuant BRAIN IQC 2024 — Stage 2 Qualifier, Top 20% globally",
      "WorldQuant BRAIN — Gold Level (Apr 2024)",
      "CAT 2023 — 96.48 percentile",
    ],

    experience: [
      {
        company: "Selección Consulting",
        role: "Full Stack Developer — AI/ML Integration",
        period: "Feb 2026 – Present",
        domain: "Enterprise GenAI platform for SAP delivery intelligence",
        context:
          "Part of the engineering team on a platform that turns SAP implementation documents into source-grounded, review-ready deliverables.",
        bullets: [
          "Contribute to a GenAI platform that transforms SOWs, RFPs, and supporting project documents into grounded scope documents, workshop plans, and RICEFW inventories for SAP implementation teams.",
          "Work on stateful LangGraph workflows covering document classification, scope and requirement extraction, structured deliverable generation, validation, and human-in-the-loop review.",
          "Build against a source-grounded RAG pipeline — document-aware chunking, Qdrant vector storage, metadata filtering, MMR context diversification, and citation-backed generation.",
          "Develop across the Python/Flask and React stack, wiring SQL project data, cloud document storage, and OpenRouter-hosted LLMs into a single workflow.",
          "Instrument LLM observability with Langfuse and OpenTelemetry — prompts, retrieved context, LangGraph state transitions, token usage, latency, errors, and user feedback.",
          "Contribute to the secure multi-client model: Entra ID SSO, project-level RBAC, and tenant-aware retrieval; currently running application security testing against the platform.",
        ],
        tech: [
          "LangGraph",
          "Qdrant",
          "Langfuse",
          "OpenTelemetry",
          "Flask",
          "React",
          "OpenRouter",
          "Entra ID",
        ],
      },
      {
        company: "QTRAMS IT Solutions",
        role: "Data Engineer",
        period: "Feb 2025 – Feb 2026",
        domain:
          "Industrial IoT, telemetry intelligence & predictive maintenance",
        bullets: [
          "Engineered real-time telemetry pipelines processing 12.5M+ events per day from 150,000+ dispensers across 30,000+ gas stations.",
          "Built a high-performance ClickHouse intelligence layer with Kafka and Airflow processing workflows, enabling sub-second analysis of device health, downtime patterns, anomaly windows, and component-level risk.",
          "Built a tool-augmented GenAI analytics capability combining embeddings, contextual retrieval, and structured ClickHouse tools for natural-language telemetry investigation and AI-assisted root-cause analysis.",
          "Grounded LLM responses in deterministic telemetry-query results and retrieved operational context, improving traceability and reducing unsupported outputs.",
          "Developed and evaluated predictive-maintenance models using ARIMA/Auto-ARIMA, LightGBM, and statistical baselines — optimising for early-warning lead time, false-alert rate, and alert stability rather than raw accuracy.",
        ],
        tech: [
          "ClickHouse",
          "Kafka",
          "Airflow",
          "Python",
          "LightGBM",
          "ARIMA",
          "Embeddings",
        ],
      },
      {
        company: "NSK Software Solutions",
        role: "AI/ML Engineer",
        period: "Aug 2023 – Jan 2025",
        domain:
          "Algorithmic trading, market intelligence & research automation",
        bullets: [
          "Built an AI/ML market research platform combining historical-data pipelines, feature engineering, forecasting, backtesting, and AI-assisted investigation across 5,000+ financial instruments.",
          "Developed a tool-augmented research assistant using embeddings, contextual retrieval, structured data-query tools, and backtesting utilities, letting users investigate features, experiments, and historical performance through grounded natural-language interaction.",
          "Engineered forecasting and anomaly-detection pipelines with LightGBM, LSTM, and statistical baselines, applying walk-forward validation and rolling backtests to test directional accuracy, signal persistence, and robustness across regimes.",
          "Established reproducible experimentation on AWS SageMaker for managed training, parameter comparison, and consistent evaluation.",
          "Designed time-series infrastructure on AWS S3, TimescaleDB, and Redshift supporting historical ingestion, feature generation, and reproducible backtesting.",
        ],
        tech: [
          "Python",
          "LightGBM",
          "LSTM",
          "SageMaker",
          "TimescaleDB",
          "Redshift",
          "S3",
        ],
      },
      {
        company: "NSK Software Solutions",
        role: "AI/ML Engineer",
        period: "Jan 2022 – Jul 2023",
        domain: "eCommerce, inventory intelligence & data products",
        bullets: [
          "Built an inventory intelligence platform combining cloud data pipelines, demand forecasting, stock-risk alerts, sales-trend analysis, and supplier-performance scoring.",
          "Engineered near-real-time inventory and order synchronisation pipelines using AWS S3, Glue, Lambda, Step Functions, SNS, and RDS.",
          "Operationalised forecasting workflows that improved inventory-planning efficiency by approximately 35%, supported by MLflow experiment tracking, data-quality validation, model monitoring, and drift detection.",
        ],
        tech: ["AWS Glue", "Lambda", "Step Functions", "RDS", "MLflow", "Python"],
      },
      {
        company: "Coal India Limited",
        role: "Operations Manager",
        period: "Aug 2016 – Nov 2021",
        domain:
          "Industrial operations, asset utilisation & procurement transformation",
        bullets: [
          "Led data-driven production planning, mine-capacity estimation, and heavy-equipment utilisation analysis using operational, maintenance, and downtime data.",
          "Managed supply-chain and procurement workflows across operations, maintenance teams, vendors, and administrative stakeholders.",
          "Drove procurement digitisation through an e-Tendering rollout, reducing procurement cycle time by roughly 50% and enabling more than ₹10 crore in annual electronic procurement.",
        ],
        tech: ["Operations", "Procurement", "Planning", "Dashboards"],
      },
    ] as Experience[],
  },

  // ---- Security & governance (dedicated section) ----
  security: {
    headline: "Security is part of the build, not a review at the end",
    intro:
      "Deploying AI systems inside an enterprise means the model is the easy part. What decides whether it ships is whether each tenant's data stays isolated, whether every generated claim can be traced to a source, and whether someone can audit what the system did last Tuesday. That work is where a forward deployed engineer earns their keep.",
    note:
      "I am actively building depth here: currently running application security testing and threat modelling against a production platform at work, and studying offensive techniques to understand what I am defending against.",
    pillars: [
      {
        title: "Identity & access",
        body:
          "Microsoft Entra ID SSO with project-level RBAC, so authorisation is enforced at the data boundary rather than in the UI.",
        items: ["Entra ID SSO", "Project-level RBAC", "Least privilege"],
      },
      {
        title: "Tenant isolation",
        body:
          "Tenant-aware retrieval so a query can only ever reach vectors and records belonging to that client — the failure mode that matters most in multi-client RAG.",
        items: ["Tenant-scoped retrieval", "Metadata filtering", "Per-client boundaries"],
      },
      {
        title: "Grounding & traceability",
        body:
          "Citation-backed generation and deterministic tool results, so outputs point at their sources and unsupported claims are visible rather than plausible.",
        items: ["Citation grounding", "Deterministic tools", "Source attribution"],
      },
      {
        title: "Observability & audit",
        body:
          "Langfuse and OpenTelemetry tracing across prompts, retrieved context, agent state transitions, token usage, latency, errors, and user feedback.",
        items: ["Langfuse", "OpenTelemetry", "Distributed tracing", "Feedback loops"],
      },
      {
        title: "Deployment control",
        body:
          "Customer-controlled deployment via site-to-site VPN and plugin or downloadable installation patterns, so clients keep their data without proprietary source exposure.",
        items: ["Site-to-site VPN", "Customer-hosted", "Source protection"],
      },
      {
        title: "Application security testing",
        body:
          "Hands-on security testing and threat modelling of a production application — auth flows, access-control boundaries, input handling, and data exposure paths.",
        items: ["Threat modelling", "Access-control testing", "Input validation"],
      },
    ],
  },

  // ---- Projects ----
  projects: [
    {
      slug: "sap-delivery-intelligence",
      title: "SAP Delivery Intelligence Platform",
      kicker: "Enterprise GenAI · Selección Consulting",
      summary:
        "A GenAI platform that turns SAP implementation documents — SOWs, RFPs, project artefacts — into source-grounded, review-ready scope documents, workshop plans, and RICEFW inventories.",
      category: "Client work",
      status: "In production",
      period: "2026 – present",
      visibility: "private",
      problem:
        "SAP project discovery starts with a pile of unstructured contractual and requirement documents. Reading them into a scope document is slow, inconsistent between consultants, and hard to audit once decisions are made.",
      approach: [
        "Stateful LangGraph workflows for document classification, scope and requirement extraction, stakeholder and timeline identification, structured deliverable generation, validation, and human review.",
        "Source-grounded RAG using document-aware chunking, embeddings, Qdrant vector storage, metadata filtering, and MMR-based context diversification.",
        "Citation-backed generation so every extracted requirement points back to the document and section it came from.",
        "Full-stack Python/Flask and React application integrating SQL project data, cloud document storage, and OpenRouter-hosted LLMs.",
        "Langfuse and OpenTelemetry tracing across prompts, retrieved context, state transitions, token usage, latency, and user feedback.",
      ],
      challenges: [
        "Enterprise artefacts are long, inconsistently structured, and full of tables — naive chunking destroys the context a requirement depends on.",
        "Consultants will not trust an extraction they cannot verify, so grounding and citation had to come before generation quality.",
        "Multiple clients on one platform means retrieval isolation is a correctness requirement, not a hardening step.",
      ],
      optimizations: [
        "Document-aware chunking that preserves section and table structure instead of splitting on token count alone.",
        "MMR-based context diversification to stop near-duplicate chunks from crowding out relevant ones.",
        "Human-in-the-loop review checkpoints placed where the model is least reliable rather than uniformly across the workflow.",
      ],
      impact:
        "Reduces manual effort in project discovery and produces deliverables a consultant can review and correct rather than write from scratch.",
      tech: [
        "LangGraph",
        "Qdrant",
        "Python",
        "Flask",
        "React",
        "Langfuse",
        "OpenTelemetry",
        "OpenRouter",
        "Entra ID",
        "SQL",
      ],
      tags: ["RAG", "Agents", "Document intelligence", "LLMOps", "Enterprise"],
    },

    {
      slug: "telemetry-intelligence",
      title: "Industrial Telemetry Intelligence",
      kicker: "IoT at scale · QTRAMS",
      summary:
        "Real-time telemetry platform processing 12.5M+ events per day from 150,000+ fuel dispensers across 30,000+ stations, with predictive maintenance and natural-language investigation on top.",
      category: "Client work",
      status: "In production",
      period: "2025 – 2026",
      visibility: "private",
      problem:
        "Dispenser failures are expensive and discovered late. The telemetry to predict them existed but arrived faster than anything downstream could query, and investigating an incident meant hand-writing SQL against billions of rows.",
      approach: [
        "Kafka and Airflow processing workflows feeding a ClickHouse intelligence layer built for sub-second analytical queries.",
        "Predictive-maintenance models using ARIMA/Auto-ARIMA, LightGBM, and statistical baselines.",
        "Tool-augmented GenAI analytics combining embeddings, contextual retrieval, and structured ClickHouse query tools for natural-language investigation.",
        "LLM responses grounded in deterministic query results and retrieved operational context rather than free generation.",
      ],
      challenges: [
        "12.5M events per day makes ingestion cost and query latency directly opposed — the schema has to serve both.",
        "Maintenance teams stop trusting an alerting system after a handful of false alarms, so false-alert rate mattered more than accuracy.",
        "Letting an LLM near operational data required constraining it to deterministic tools rather than free-form SQL generation.",
      ],
      optimizations: [
        "ClickHouse partitioning and materialised views shaped around the actual investigation queries, not generic schema design.",
        "Models evaluated on early-warning lead time, false-alert rate, and alert stability — the metrics operations teams act on.",
        "Structured tool interfaces that let the model choose the query but not write it.",
      ],
      impact:
        "Sub-second analysis of device health, downtime patterns, anomaly windows, and component-level risk, with AI-assisted root-cause investigation available to non-SQL users.",
      metrics: [
        { value: "12.5M+", label: "events / day" },
        { value: "150k+", label: "dispensers" },
        { value: "30k+", label: "stations" },
      ],
      tech: [
        "ClickHouse",
        "Kafka",
        "Airflow",
        "Python",
        "LightGBM",
        "ARIMA",
        "Embeddings",
      ],
      tags: ["Streaming", "Predictive maintenance", "Anomaly detection", "GenAI"],
    },

    {
      slug: "atlas-agentic-data-engineering",
      title: "Atlas — Agentic Data Engineering",
      kicker: "Self-operating pipelines · chain-08",
      summary:
        "An agent-driven data platform that orchestrates end-of-day market-data ingestion, validates it, detects incidents, and walks an operator through recovery — with every action auditable.",
      category: "Platform",
      status: "Active build",
      period: "2025 – present",
      visibility: "private",
      problem:
        "EOD ingestion across multiple market datasets fails in boring, recurring ways. Each failure costs a morning of manual diagnosis before anyone can trust the day's data.",
      approach: [
        "Polls Airflow, exposes Prometheus metrics, and posts a daily digest to Slack at a fixed morning slot.",
        "Tool-based workflows for pipeline-health checks, failure diagnosis, and operational recovery actions.",
        "Metadata lineage so a failed downstream table can be traced back to the ingestion that broke it.",
        "Auditable execution — every agent action is recorded rather than silently applied.",
      ],
      challenges: [
        "An agent with write access to production pipelines needs guardrails before it needs capability.",
        "Distinguishing a genuinely failed ingest from an upstream vendor being late required lineage, not just status codes.",
      ],
      impact:
        "Automated EOD ingestion and validation with guided recovery, replacing manual morning triage.",
      tech: [
        "Python",
        "Airflow",
        "ClickHouse",
        "Prometheus",
        "Slack API",
        "Docker",
        "FastAPI",
      ],
      tags: ["Agentic AI", "Data quality", "Observability", "Automation"],
    },

    {
      slug: "lens-research-platform",
      title: "Lens — AI-Assisted Research Platform",
      kicker: "Quant research · chain-08",
      summary:
        "Extends Atlas into a quantitative research environment: time-series pipelines, feature engineering, forecasting, backtesting, and LLM-assisted investigation over the results.",
      category: "Platform",
      status: "Active build",
      period: "2025 – present",
      visibility: "private",
      approach: [
        "Time-series data pipelines and feature engineering feeding reproducible experiments.",
        "Forecasting and backtesting with structured analytics tools exposed to an LLM layer.",
        "Grounded investigation workflows so researchers query experiments and market signals in natural language against real results.",
      ],
      challenges: [
        "Research answers must be reproducible — an LLM summarising a backtest is only useful if the backtest itself is deterministic.",
      ],
      impact:
        "Lets a researcher move from question to evidence without rebuilding the pipeline each time.",
      tech: ["Python", "LightGBM", "LSTM", "Backtesting", "ClickHouse", "LLM tools"],
      tags: ["GenAI", "Finance", "Research", "Backtesting"],
    },

    {
      slug: "trade-research-agent",
      title: "Trade Research Agent",
      kicker: "Systematic research infrastructure",
      summary:
        "Market-data and quantitative research infrastructure spanning NSE, TSX, and US equities — daily ingestion running in production, with the research layer still being validated.",
      category: "Product",
      status: "Research",
      period: "2026 – present",
      visibility: "private",
      problem:
        "Answering evidence-based questions — which liquid NSE stocks suit a given position size, which features actually carry predictive value — requires infrastructure before it requires models.",
      approach: [
        "FastAPI and React application over PostgreSQL/TimescaleDB with Redis caching.",
        "Dagster-orchestrated daily ingestion from yfinance, running in production.",
        "Artifact-backed research views for features, factors, and model experiments.",
      ],
      challenges: [
        "Honest scoping: daily ingestion is live, but factor and model pages still depend on artifacts that are not fully wired, and some views carry fallback behaviour. This is research infrastructure being built toward validation, not a finished platform.",
      ],
      impact:
        "Provides the reproducible data foundation required before any strategy claim can be taken seriously.",
      tech: [
        "FastAPI",
        "React",
        "TypeScript",
        "PostgreSQL",
        "TimescaleDB",
        "Redis",
        "Dagster",
        "Python",
      ],
      tags: ["Quant", "Time series", "Data platform", "In progress"],
    },

    {
      slug: "rill-real-estate-research",
      title: "RILL — Real Estate Market Research",
      kicker: "Investment discovery · full stack",
      summary:
        "Analyses localities within roughly 50 km of Hyderabad and scores them for investment potential, with the scoring logic exposed rather than hidden behind a number.",
      category: "Product",
      status: "Active build",
      period: "2026",
      visibility: "private",
      problem:
        "Locality-level property research is scattered across listings, news, and hearsay, and most tools give a score without explaining it.",
      approach: [
        "FastAPI backend with a Next.js frontend over PostgreSQL persistence.",
        "Pluggable trend collectors, including an HTML table collector for public pages that publish trend data.",
        "Transparent investment scoring where each contributing signal is visible.",
        "Ships with seed market signals so the product works without depending on fragile scraping.",
      ],
      challenges: [
        "Property data sources are inconsistent and often unscrapeable, so the collector architecture had to degrade gracefully rather than break.",
      ],
      tech: ["FastAPI", "Next.js", "TypeScript", "PostgreSQL", "Python", "Docker"],
      tags: ["Full stack", "Data collection", "Scoring", "Real estate"],
    },

    {
      slug: "digital-gold",
      title: "Digital Gold — AI Jewellery Catalogue",
      kicker: "Commerce platform · monorepo",
      summary:
        "An AI-assisted jewellery catalogue platform built around discovery, recommendations, and lead generation, structured as a production-shaped monorepo.",
      category: "Product",
      status: "Active build",
      period: "2026",
      visibility: "private",
      approach: [
        "Next.js storefront covering catalogue browsing, an AI chat interface, and lead capture.",
        "Modular Node.js API with background workers handling indexing, embeddings, and notifications.",
        "Shared type and contract package keeping frontend, API, and workers in sync.",
      ],
      challenges: [
        "Recommendation quality in jewellery is visual and subjective, which makes embedding choice matter more than model size.",
      ],
      tech: ["Next.js", "TypeScript", "Node.js", "Embeddings", "Monorepo"],
      tags: ["Full stack", "AI recommendations", "eCommerce"],
    },

    {
      slug: "growth-operating-system",
      title: "Growth — Personal Operating System",
      kicker: "Adaptive planning product",
      summary:
        "A system that turns ambitions into a personalised plan and keeps revising it against real-world evidence, instead of stopping at goal and task creation like most tools do.",
      category: "Product",
      status: "Active build",
      period: "2026",
      visibility: "private",
      problem:
        "Goal-setting apps capture intent and then go quiet. They never model where someone actually is, what constrains them, or which actions are producing results.",
      approach: [
        "Models current state, target state, motivation, and constraints before generating any plan.",
        "Builds a personalised operating system per user and revises it using observed outcomes.",
        "Domain-adaptive: fitness, career transitions, product building, and skill acquisition each get different plan structures.",
      ],
      challenges: [
        "The hard problem is evidence, not planning — deciding which signals prove an action worked and which are noise.",
      ],
      tech: ["Python", "LLMs", "Adaptive planning"],
      tags: ["Product", "GenAI", "Personalisation"],
    },

    {
      slug: "inglorious-crypto",
      title: "Inglorious Crypto — Rust Streaming Pipeline",
      kicker: "Low-latency ingestion in Rust",
      summary:
        "Real-time cryptocurrency pipeline in Rust: Binance WebSocket trades flow through Kafka into QuestDB for time-series querying.",
      category: "Open source",
      status: "Archived",
      period: "2025",
      visibility: "public",
      repo: "https://github.com/ravic8/inglorious_crypto",
      approach: [
        "Rust WebSocket fetcher subscribed to Binance BTC/USDT trade updates.",
        "Kafka producer and consumer decoupling ingestion from storage.",
        "QuestDB sink for fast time-series queries over trade history.",
      ],
      challenges: [
        "Written in Rust specifically to hold latency and memory predictable under sustained trade volume.",
      ],
      tech: ["Rust", "Kafka", "QuestDB", "WebSockets", "Docker"],
      tags: ["Rust", "Streaming", "Crypto", "Real-time"],
    },

    {
      slug: "tradelens",
      title: "TradeLens — Kafka & Flink Architecture",
      kicker: "Event-driven stream processing",
      summary:
        "Event-driven BTC/USDT pipeline design and implementation: Binance WebSocket → Kafka → Flink → QuestDB, with the architectural trade-offs documented.",
      category: "Open source",
      status: "Archived",
      period: "2025",
      visibility: "public",
      repo: "https://github.com/ravic8/TradeLens",
      approach: [
        "Microservices-based event-driven architecture with Kafka buffering real-time trade events.",
        "Flink handling stream transformation and analytics before storage.",
        "QuestDB for time-series storage, with Grafana for visualisation.",
      ],
      tech: ["Python", "Kafka", "Flink", "QuestDB", "Grafana", "Docker Compose"],
      tags: ["Streaming", "Flink", "Architecture"],
    },

    {
      slug: "lakehouse-pipeline",
      title: "Kafka · Spark · Hudi Lakehouse",
      kicker: "Batch + streaming lakehouse",
      summary:
        "Data pipeline integrating Kafka, Spark, and Apache Hudi — incremental upserts and time-travel queries over a streaming ingestion layer.",
      category: "Open source",
      status: "Archived",
      period: "2024",
      visibility: "public",
      repo: "https://github.com/ravic8/data_pipeline_project",
      approach: [
        "Kafka ingestion feeding Spark structured streaming jobs.",
        "Apache Hudi tables providing incremental upserts and time travel over the lake.",
      ],
      tech: ["Python", "Kafka", "Spark", "Apache Hudi"],
      tags: ["Lakehouse", "Spark", "Streaming"],
    },

    {
      slug: "fmp-api",
      title: "FMP API — Financial Data Ingestion",
      kicker: "Market data ETL",
      summary:
        "Fetches, filters, and persists Financial Modeling Prep data — filtered to Indian exchanges (NSE, BSE) — into CSV and PostgreSQL, with Docker-based local infrastructure.",
      category: "Open source",
      status: "Archived",
      period: "2024",
      visibility: "public",
      repo: "https://github.com/ravic8/fmp_api",
      approach: [
        "API ingestion with exchange-level filtering for NSE and BSE listings.",
        "Dual persistence to CSV and PostgreSQL.",
        "Docker Compose setup for PostgreSQL and pgAdmin, with environment-based secret handling.",
      ],
      tech: ["Python", "PostgreSQL", "Docker", "REST APIs"],
      tags: ["ETL", "Market data", "Ingestion"],
    },

    {
      slug: "c8-datalake",
      title: "C8 Datalake — Infrastructure as Code",
      kicker: "Terraform · ClickHouse · CI/CD",
      summary:
        "Terraform-provisioned ClickHouse lake with GitHub Actions deployment, automated log shipping to S3, and scheduled Docker and system cleanup.",
      category: "Platform",
      status: "In production",
      period: "2025",
      visibility: "private",
      approach: [
        "Terraform-managed infrastructure with ClickHouse configuration held in version control.",
        "GitHub Actions pipelines handling deployment end to end.",
        "Automated log upload to S3 and scheduled Docker/system cleanup to keep hosts healthy.",
      ],
      tech: ["Terraform", "ClickHouse", "GitHub Actions", "Docker", "AWS S3", "Shell"],
      tags: ["Infrastructure", "IaC", "CI/CD"],
    },

    {
      slug: "inventory-intelligence",
      title: "Inventory Intelligence Platform",
      kicker: "Demand forecasting · eCommerce",
      summary:
        "Cloud pipelines, demand forecasting, stock-risk alerts, and supplier scoring for aftermarket parts eCommerce, improving planning efficiency by roughly 35%.",
      category: "Client work",
      status: "In production",
      period: "2022 – 2023",
      visibility: "private",
      approach: [
        "Near-real-time inventory and order synchronisation on AWS S3, Glue, Lambda, Step Functions, SNS, and RDS.",
        "MLflow-tracked demand forecasting with supplier-performance scoring.",
        "Data-quality validation, model monitoring, and drift detection around the forecasting workflows.",
      ],
      impact:
        "Improved inventory-planning efficiency by approximately 35% and automated procurement alerting.",
      metrics: [{ value: "~35%", label: "planning efficiency gain" }],
      tech: [
        "Python",
        "AWS Glue",
        "Lambda",
        "Step Functions",
        "RDS",
        "MLflow",
      ],
      tags: ["Forecasting", "Supply chain", "AWS", "MLOps"],
    },

    {
      slug: "agentic-lab",
      title: "Agentic Lab",
      kicker: "LangGraph patterns, from scratch",
      summary:
        "A working lab for building LangGraph workflows without scaffolding — intent classification, retrieval-or-tool routing, drafting, validation, and fallback as an explicit state graph.",
      category: "Lab",
      status: "Active build",
      period: "2026",
      visibility: "private",
      approach: [
        "Deliberately small graph: classify intent → route to retrieval or tool → draft → validate → fallback or finish.",
        "Focus on state, conditional edges, validation, and fallback behaviour rather than framework surface area.",
        "Prompt logging kept throughout to make the workflow's decisions inspectable.",
      ],
      tech: ["LangGraph", "Python", "RAG", "Tool calling"],
      tags: ["LangGraph", "Agents", "Learning"],
    },
  ] as Project[],

  // ---- Blogs (none published yet; Blog route stays available) ----
  blogs: [] as Blog[],

  // ---- Contact ----
  contact: {
    hero_title: "Deploy me against a hard problem",
    hero_description:
      "I am open to forward deployed, AI systems, and platform engineering roles, and to consulting engagements where someone needs a single person accountable for a product from data to interface.",
    headline: "Deploy me against a hard problem.",
    availability:
      "Open to forward deployed engineering, AI systems, and platform roles — full-time or consulting.",
    location: "India (IST, UTC+5:30)",
    note: "Email or LinkedIn work best. Happy to start with a scoped prototype.",
  },
};

export type SiteConfig = typeof siteConfig;
